import React, { useState } from 'react';

import { useForm } from 'react-hook-form';
import { Button, Dropdown, TextInput } from 'Profile/components';
import { useTranslation } from 'hooks';

export default function GuestInformation({ propertyContent, guestInfoValues, onGoBack, onSubmit }) {
	const { t } = useTranslation();

	const { handleSubmit, register } = useForm({
		defaultValues: {},
	});

	const [reservationHolder, ...fixedGuests] = guestInfoValues.filter(
		({ createdByGuest }) => !createdByGuest
	);

	// Cannot be edited
	const fixedAdultGuests = fixedGuests.filter(({ isMinor }) => !isMinor);
	const fixedChildGuests = fixedGuests.filter(({ isMinor }) => isMinor);

	// Can be edited
	const adultGuests = guestInfoValues.filter(
		({ createdByGuest, isMinor }) => createdByGuest && !isMinor
	);
	const childGuests = guestInfoValues.filter(
		({ createdByGuest, isMinor }) => createdByGuest && isMinor
	);

	const [childFields, setChildFields] = useState(childGuests);

	const numberOfChildFields = fixedChildGuests.length + childFields.length;

	const minChildAge = parseInt(propertyContent.minChildAge || 0, 10);
	const maxChildAge = parseInt(propertyContent.maxChildAge || '16', 10);

	const childAges = Array.from({ length: maxChildAge - minChildAge }, (_, i) => minChildAge + i);

	const currentYear = new Date().getFullYear();
	const birthYears = childAges.map(age => (currentYear - age).toString());

	const handleOnSubmit = formData => {
		const guestsFromFormData = formData.guests || [];
		const processedFormData = guestsFromFormData
			.filter(guest => guest !== null)
			.map(guest => {
				const processedGuest = {};
				let guestId = null;

				// If existing profile, grab id from name={name--id}
				Object.entries(guest).forEach(([key, value]) => {
					if (key.includes('--')) {
						const [fieldName, id] = key.split('--');
						processedGuest[fieldName] = value;
						guestId = id;
					} else {
						processedGuest[key] = value;
					}
				});

				const result = { ...processedGuest };
				if (guestId) {
					result.id = guestId;
				}

				result.isMinor = result.birthYear !== undefined;

				if (result.birthYear) {
					result.birthYear = parseInt(result.birthYear, 10);
				}

				return result;
			});

		onSubmit(processedFormData);
	};

	return (
		<form onSubmit={handleSubmit(handleOnSubmit)} className="fs-trip__arrival-details__guest-form">
			<div>
				<div className="ty-c1">{`${t('Guest 1')} | ${t('Reservation holder')}`}</div>
				<div className="fs-trip__arrival-details__guest-form__holder-name">
					{`${reservationHolder?.firstName}, ${reservationHolder?.lastName}`}
				</div>
			</div>
			{fixedAdultGuests.map(({ id, firstName, lastName }, index) => {
				const guestNumber = index + 2;
				return (
					<div key={id}>
						<div className="ty-c1">{`${t('Guest')} ${guestNumber} | ${t('Adult')}`}</div>
						<div className="fs-trip__arrival-details__guest-form__holder-name">
							{`${firstName}, ${lastName}`}
						</div>
					</div>
				);
			})}
			{fixedChildGuests.map(({ id, firstName, birthYear }, index) => {
				const guestNumber = fixedAdultGuests.length + index + 2;
				return (
					<div key={id}>
						<div className="ty-c1">{`${t('Guest')} ${guestNumber} | ${t('Child')}`}</div>
						<div className="fs-trip__arrival-details__guest-form__holder-name">
							{`${firstName}, ${birthYear}`}
						</div>
					</div>
				);
			})}

			{adultGuests
				.map(({ id, firstName, lastName }, index) => {
					const guestNumber = fixedGuests.length + index + 2;
					const fieldArray = `guests[${guestNumber}]`;
					return (
						<div key={id || index}>
							<div className="ty-c1">{t(`Guest ${guestNumber} | Adult`)}</div>
							<div className="fs-trip__arrival-details__guest-form__guest-fields">
								<TextInput
									name={`${fieldArray}.firstName${id ? `--${id}` : ''}`}
									placeholder={t('First Name')}
									defaultValue={firstName}
									register={register}
								/>
								<TextInput
									name={`${fieldArray}.lastName${id ? `--${id}` : ''}`}
									placeholder={t('Last Name')}
									defaultValue={lastName}
									register={register}
								/>
							</div>
						</div>
					);
				})
				.map(inputs => inputs)}

			{childFields
				.map(({ id, firstName, birthYear }, index) => {
					const guestNumber = fixedGuests.length + adultGuests.length + index + 2;
					const fieldArray = `guests[${guestNumber}]`;
					return (
						<div key={id || index}>
							<div className="ty-c1 fs-trip__arrival-details__guest-form__child-label">
								{t(`Guest ${guestNumber} | Child`)}
								<Button
									kind="link"
									type="button"
									onClick={() => {
										setChildFields(prev => {
											if (id) {
												return prev.filter(guest => guest.id !== id);
											}
											return prev.filter((_, i) => i !== index);
										});
									}}
									className="ty-c4 fs-trip__arrival-details__guest-form__remove-btn"
								>
									{t('Remove')}
								</Button>
							</div>
							<div className="fs-trip__arrival-details__guest-form__guest-fields">
								<TextInput
									name={`${fieldArray}.firstName${id ? `--${id}` : ''}`}
									placeholder={t('First Name')}
									defaultValue={firstName}
									register={register}
								/>
								<Dropdown
									label={t('Birth Year')}
									name={`${fieldArray}.birthYear${id ? `--${id}` : ''}`}
									options={birthYears}
									register={register}
									defaultValue={birthYear.toString()}
								/>
							</div>
						</div>
					);
				})
				.map(inputs => inputs)}

			{numberOfChildFields < 5 && (
				<div className="fs-trip__arrival-details__guest-form__add-child">
					<div className="fs-trip__arrival-details__guest-form__add-child__btn">
						<i aria-hidden="true" className="icon icon-plus" />
						<Button
							type="button"
							kind="link"
							onClick={() => {
								setChildFields(prev => [
									...prev,
									{
										firstName: '',
										birthYear: '',
									},
								]);
							}}
						>
							{t('Add child')}
						</Button>
					</div>
				</div>
			)}

			<div className="fs-trip__arrival-details__btns">
				{onGoBack ? (
					<>
						<Button type="button" kind="secondary" onClick={onGoBack} aria-label="">
							{t('Back')}
						</Button>
						<Button type="submit" className="CTA--secondary--dark" aria-label="">
							{t('Save')}
						</Button>
					</>
				) : (
					<Button type="submit" className="CTA--secondary--dark" aria-label="">
						{t('Update')}
					</Button>
				)}
			</div>
		</form>
	);
}
